/**
 * InstantPayoutModal — Shopify-style "Instant Payout to Debit Card" flow.
 *
 * Opens from the Cash tab on VendorEarningsDashboard. Shows:
 *   • Big available-balance figure
 *   • Amount input (default: pay out all) + Max button
 *   • Live fee preview (1.5%, min $0.50) → net amount vendor receives
 *   • Debit-card destination preview ("Visa ending ••••1234")
 *   • Primary CTA: "Pay out now" → calls POST /vendor/payout-wizard/payout
 *     with method='instant', amount_cents
 *   • Secondary: "Standard (free, 2–3 days)" link
 *   • Success state: animated green receipt with payout_id + arrival ETA
 *
 * If the vendor has no debit card attached (external_account_type !==
 * 'card'), we show an inline CTA to open the Bank tab where the embedded
 * <ConnectAccountManagement/> lets them add one — without leaving our app.
 *
 * This replaces the ugly `window.confirm()` prompt that was there before.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { Zap, X, Check, CreditCard, Loader2, Info, ArrowRight, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INSTANT_FEE_PCT = 0.015;   // Stripe charges 1.5% on instant payouts
const INSTANT_FEE_MIN_CENTS = 50;

function fmt(amount, currency = 'usd') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase(), minimumFractionDigits: 2 }).format(amount || 0);
}

function Receipt({ receipt, currency, onClose }) {
  // Prefer the server-returned amount (it may have been trimmed to the
  // live Stripe balance). Fee honours the 1.5% rate AND the min-$0.50
  // floor so the UI never underquotes what Stripe actually took.
  const gross = Number(receipt.amount ?? 0);
  const fee = Math.max(gross * INSTANT_FEE_PCT, INSTANT_FEE_MIN_CENTS / 100);
  const net = Math.max(gross - fee, 0);
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      className="relative p-6 rounded-2xl bg-gradient-to-br from-emerald-500/25 via-lime-500/15 to-emerald-900/40 border border-emerald-400/40 overflow-hidden"
      data-testid="instant-payout-receipt"
    >
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
        className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-emerald-400/20 blur-2xl"
      />
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-full bg-emerald-400/25 border border-emerald-300/50 flex items-center justify-center">
            <Check size={22} className="text-emerald-200" strokeWidth={2.5} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-bold">Instant Payout Sent</p>
            <h3 className="font-syne text-xl font-bold text-white">Money on its way</h3>
          </div>
          <PartyPopper size={18} className="text-amber-300 ml-auto" />
        </div>

        <div className="grid grid-cols-2 gap-x-5 gap-y-3 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/50">Gross</p>
            <p className="text-white font-semibold">{fmt(gross, currency)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/50">Stripe fee (1.5%, min $0.50)</p>
            <p className="text-white/70">− {fmt(fee, currency)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/50">Net to your card</p>
            <p className="text-emerald-200 font-bold text-base" data-testid="receipt-net">{fmt(net, currency)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/50">Arrives in</p>
            <p className="text-white font-semibold">~30 minutes</p>
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-[11px] text-white/50">
          <span>Payout <span className="font-mono text-white/70">{(receipt.payout_id || '').slice(-8)}</span></span>
          <span className="inline-flex items-center gap-1">Status: <span className="capitalize text-emerald-300">{receipt.status || 'pending'}</span></span>
        </div>
      </div>

      <button
        onClick={onClose}
        className="absolute top-3 right-3 p-1.5 rounded-lg text-white/60 hover:bg-white/10 hover:text-white"
      >
        <X size={14} />
      </button>
    </motion.div>
  );
}

export default function InstantPayoutModal({ open, onClose, available, currency = 'usd', settings, getAuthHeaders, onPayoutComplete, onOpenBank }) {
  const [amount, setAmount] = useState(available || 0);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    if (open) {
      setAmount(available || 0);
      setReceipt(null);
    }
  }, [open, available]);

  const hasCard = settings?.external_account_type === 'card';
  const payoutsEnabled = !!settings?.payouts_enabled;
  const instantAvailable = Number(settings?.instant_available_amount ?? available ?? 0);

  const { feeDollars, netDollars } = useMemo(() => {
    const dollars = Number(amount) || 0;
    const fee = Math.max(dollars * INSTANT_FEE_PCT, INSTANT_FEE_MIN_CENTS / 100);
    return { feeDollars: fee, netDollars: Math.max(dollars - fee, 0) };
  }, [amount]);

  const amountCents = Math.round(Number(amount) * 100);
  const canSubmit = hasCard && payoutsEnabled && amountCents > 0 && amountCents <= Math.round(instantAvailable * 100) && !busy;

  const triggerInstant = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/vendor/payout-wizard/payout`,
        { method: 'instant', amount_cents: amountCents },
        { headers: getAuthHeaders() },
      );
      setReceipt(r.data);
      toast.success('Instant payout sent — arrives in ~30 minutes');
      onPayoutComplete?.(r.data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Instant payout failed';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const triggerStandard = async () => {
    if (!payoutsEnabled || amountCents <= 0) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/vendor/payout-wizard/payout`,
        { method: 'standard', amount_cents: amountCents },
        { headers: getAuthHeaders() },
      );
      toast.success('Standard payout queued — arrives in 2–3 business days');
      onPayoutComplete?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Standard payout failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          data-testid="instant-payout-modal"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', duration: 0.4 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-2xl bg-[#13153A] border border-white/10 shadow-[0_30px_80px_rgba(139,92,246,0.3)] overflow-hidden"
          >
            {/* Header */}
            <div className="relative p-6 pb-4 border-b border-white/5 bg-gradient-to-br from-lime-500/10 via-transparent to-transparent">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-1.5 rounded-lg text-white/60 hover:bg-white/10 hover:text-white"
                data-testid="instant-payout-close"
              >
                <X size={16} />
              </button>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-lime-400/30 to-emerald-500/30 border border-lime-400/40 flex items-center justify-center">
                  <Zap size={18} className="text-lime-300 fill-lime-300" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-lime-300 font-bold">Instant Payout</p>
                  <h2 className="font-syne text-xl font-bold text-white">Cash out to debit card</h2>
                </div>
              </div>
              <p className="text-xs text-white/55">
                Stripe will send your money to your attached debit card in ~30 minutes. 1.5% fee (min $0.50) is deducted from the payout.
              </p>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {receipt ? (
                <>
                  <Receipt receipt={receipt} currency={currency} onClose={onClose} />
                  <button
                    onClick={onClose}
                    className="w-full py-3 rounded-xl text-sm font-bold text-white bg-white/5 border border-white/10 hover:bg-white/10"
                    data-testid="instant-payout-done"
                  >
                    Done
                  </button>
                </>
              ) : !hasCard ? (
                <div className="rounded-xl p-5 bg-amber-500/10 border border-amber-400/30 space-y-3" data-testid="instant-payout-no-card">
                  <div className="flex items-start gap-3">
                    <CreditCard size={18} className="text-amber-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-amber-200">Add a debit card to go instant</p>
                      <p className="text-xs text-amber-100/80 mt-1">
                        Stripe needs a debit card (not a bank account) to send money in minutes. Add one in the Bank tab and come straight back here.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => { onOpenBank?.(); onClose?.(); }}
                    className="w-full py-3 rounded-xl text-sm font-bold text-[#06061A] bg-gradient-to-r from-lime-300 to-emerald-400 hover:brightness-110"
                    data-testid="instant-payout-add-card"
                  >
                    Add a debit card <ArrowRight size={14} className="inline ml-1" />
                  </button>
                  <button
                    onClick={triggerStandard}
                    disabled={!payoutsEnabled || amountCents <= 0 || busy}
                    className="w-full py-2 text-xs text-amber-200/80 hover:text-amber-100 underline underline-offset-4 disabled:opacity-40"
                  >
                    Or take the free 2–3 day standard payout
                  </button>
                </div>
              ) : (
                <>
                  {/* Available balance */}
                  <div className="rounded-xl bg-gradient-to-br from-lime-500/10 to-emerald-900/5 border border-lime-400/15 p-4">
                    <p className="text-[10px] uppercase tracking-wider text-lime-300 font-bold">Instantly available</p>
                    <p className="text-2xl font-bold text-white font-syne mt-0.5" data-testid="instant-payout-available">
                      {fmt(instantAvailable, currency)}
                    </p>
                    <p className="text-[11px] text-white/50 mt-1">
                      {settings?.external_account_bank || 'Debit card'} •••• {settings?.external_account_last4 || '----'}
                    </p>
                  </div>

                  {/* Amount input */}
                  <div className="space-y-2">
                    <label className="text-[11px] uppercase tracking-wider text-white/55 font-bold">Payout amount</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 text-lg">$</span>
                      <input
                        type="number" min="1" max={instantAvailable} step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="w-full h-14 pl-9 pr-24 rounded-xl bg-white/[0.04] border border-white/10 text-xl font-bold text-white focus:outline-none focus:border-lime-400 focus:ring-2 focus:ring-lime-400/20"
                        data-testid="instant-payout-amount"
                      />
                      <button
                        onClick={() => setAmount(instantAvailable)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-lime-400/15 text-lime-300 border border-lime-400/25 hover:bg-lime-400/25"
                        data-testid="instant-payout-max"
                      >
                        Max
                      </button>
                    </div>
                  </div>

                  {/* Fee preview */}
                  <div className="rounded-xl bg-white/[0.03] border border-white/10 divide-y divide-white/5">
                    <div className="flex items-center justify-between px-4 py-3 text-sm">
                      <span className="text-white/55">Payout</span>
                      <span className="text-white font-semibold">{fmt(amount, currency)}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 text-sm">
                      <span className="text-white/55 inline-flex items-center gap-1.5">
                        Stripe fee <Info size={11} className="opacity-50" title="1.5%, minimum $0.50" />
                      </span>
                      <span className="text-white/70">− {fmt(feeDollars, currency)}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 bg-lime-500/5">
                      <span className="text-sm font-bold text-lime-300">You receive</span>
                      <span className="font-syne text-lg font-bold text-white" data-testid="instant-payout-net">{fmt(netDollars, currency)}</span>
                    </div>
                  </div>

                  {/* CTA */}
                  <button
                    onClick={triggerInstant}
                    disabled={!canSubmit}
                    className="w-full py-4 rounded-xl text-sm font-bold uppercase tracking-wider text-[#06061A] bg-gradient-to-r from-lime-300 via-lime-400 to-emerald-400 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_8px_30px_rgba(163,230,53,0.25)] inline-flex items-center justify-center gap-2"
                    data-testid="instant-payout-submit"
                  >
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} className="fill-current" />}
                    {busy ? 'Sending…' : `Pay out ${fmt(amount, currency)} now`}
                  </button>

                  <button
                    onClick={triggerStandard}
                    disabled={!payoutsEnabled || amountCents <= 0 || busy}
                    className="w-full py-2 text-xs text-white/55 hover:text-white underline underline-offset-4 disabled:opacity-40"
                    data-testid="instant-payout-use-standard"
                  >
                    Prefer the free 2–3 day standard payout?
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
